import React, { useState, useEffect, useRef } from "react";
import Menu from "../Menu";
import { useNavigate, Link } from "react-router-dom";

// Load Chart.js
const loadChartJS = () => {
  return new Promise((resolve) => {
    if (window.Chart) { resolve(window.Chart); return; }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.js';
    script.onload = () => resolve(window.Chart);
    script.onerror = () => {
      console.error('Failed to load Chart.js from CDN');
      // Fallback: try a different CDN or local version
      const fallbackScript = document.createElement('script');
      fallbackScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.0/chart.umd.js';
      fallbackScript.onload = () => resolve(window.Chart);
      fallbackScript.onerror = () => {
        console.error('Failed to load Chart.js from fallback CDN');
        resolve(null); // Resolve with null to prevent blocking
      };
      document.head.appendChild(fallbackScript);
    };
    document.head.appendChild(script);
  });
};

/* ─── Theme function ─────────────────────────── */
const getTheme = () => ({
  bg: 'var(--bg)',
  surface: 'var(--surface)',
  surface2: 'var(--surface2)',
  border: 'var(--border)',
  text: 'var(--text)',
  textMuted: 'var(--text-muted)',
  accent: 'var(--info)',
  info: 'var(--info)',
  warning: 'var(--warning)',
  danger: 'var(--danger)',
  accent2: 'var(--info2)',
  accent3: 'var(--info3)',
});

const Schedule_COLORS = ['#f59e0b', '#3b82f6', '#a855f7', '#10b981', '#ef4444'];

/* ─── Donut Chart via SVG ─── */
const DonutChart = ({ pct, color }) => {
  const theme = getTheme();
  const r = 42, cx = 54, cy = 54;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  const gap  = circ - dash;
  return (
    <svg width={108} height={108} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={theme.surface2} strokeWidth={10} />
      <circle
        cx={cx} cy={cy} r={r} fill="none"
        stroke={color} strokeWidth={10}
        strokeDasharray={`${dash} ${gap}`}
        strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 0.6s ease' }}
      />
      <text
        x={cx} y={cy + 6}
        textAnchor="middle"
        fill={color}
        fontSize={16}
        fontWeight={700}
        fontFamily="'DM Sans', sans-serif"
        style={{ transform: 'rotate(90deg)', transformOrigin: `${cx}px ${cy}px` }}
      >
        {pct}%
      </text>
    </svg>
  );
};

/* ─── Bar chart (Chart.js canvas) ─── */
const CatelogStChart = ({ data, period }) => {
  const theme = getTheme();
  const canvasRef = useRef(null);
  const chartRef  = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => { loadChartJS().then(() => setReady(true)); }, []);

  useEffect(() => {
    if (!ready || !canvasRef.current || !data?.length || !window.Chart) return;
    if (chartRef.current) chartRef.current.destroy();

    // Check if data has totalsecond (Report11 format) to show 2 bars per record
    const hasTotalSeconds = data.some(d => d.totalsecond !== undefined);
    
    let labels, datasets;
    
    if (hasTotalSeconds) {
      // Report11 data - 2 bars per record (totalsecond + avgrunningtimeinseconds)
      labels = data.map((item, index) => `${item.catalogname || `Item ${index + 1}`} - ${index + 1}`);
      const totalSeconds = data.map(item => (item.totalsecond || 0) / 3600);
      const avgRunningTime = data.map(item => (item.avgrunningtimeinseconds || 0) / 3600);
      
      datasets = [
        {
          label: 'Total Seconds (hrs)',
          data: totalSeconds,
          backgroundColor: 'rgba(139, 92, 246, 0.6)',  // Purple
          borderColor: '#8b5cf6',
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
        },
        {
          label: 'Avg Running Time (hrs)',
          data: avgRunningTime,
          backgroundColor: 'rgba(0, 212, 170, 0.7)',  // Green
          borderColor: '#00d4aa',
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
        },
      ];
    } else {
      // Report10 data - 1 bar per record with only purple color
      labels = data.map((item, index) => item.catalogname || `Item ${index + 1}`);
      const totalHours = data.map(item => (item.totalrunningtime || 0) / 3600);
      
      datasets = [
        {
          label: 'Total Running Time (hrs)',
          data: totalHours,
          backgroundColor: 'rgba(139, 92, 246, 0.6)',  // Purple only
          borderColor: '#8b5cf6',
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
        },
      ];
    }
    
    chartRef.current = new window.Chart(canvasRef.current.getContext('2d'), {
      type: 'bar',
      data: {
        labels,
        datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
            labels: {
              color: theme.textMuted,
              font: { family: 'DM Sans', size: 12 },
              usePointStyle: true,
              pointStyle: 'rect',
              padding: 20,
            },
          },
          tooltip: {
            callbacks: {
              label: ctx => {
                const h = Math.floor(ctx.parsed.y);
                const m = Math.round((ctx.parsed.y - h) * 60);
                return ` ${ctx.dataset.label}: ${h}h ${String(m).padStart(2,'0')}m`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { color: 'rgba(255,255,255,0.04)', drawBorder: false },
            ticks: { color: theme.textMuted, font: { family: 'DM Sans', size: 12 } },
          },
          y: {
            grid: { color: 'rgba(255,255,255,0.06)', drawBorder: false },
            ticks: {
              color: theme.textMuted,
              font: { family: 'DM Sans', size: 11 },
              callback: v => `${v}`,
            },
            beginAtZero: true,
          },
        },
      },
    });
    return () => chartRef.current?.destroy();
  }, [ready, data]);

  const periodLabel = { 
  today: 'Today', 
  thisweek: 'This Week', 
  last7days: 'Last 7 Days', 
  last30days: 'Last 30 Days', 
  month: 'This Month', 
  all: 'All Time', 
  custom: 'Custom' 
  }[period] || '';

  return (
    <div style={{
      background: theme.surface, border: `1px solid ${theme.border}`,
      borderRadius: 16, padding: '24px 28px', marginBottom: 28,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
        <span style={{ fontSize: 22, color: theme.accent }}>≡</span>
        <span style={{
          background: theme.surface2, border: `1px solid ${theme.border}`,
          borderRadius: 6, padding: '2px 10px',
          color: theme.text, fontWeight: 600, fontSize: 15,
        }}>Schedule Performance</span>
        <span style={{ color: theme.text, fontWeight: 600, fontSize: 15 }}>— {periodLabel}</span>
      </div>
      {!data?.length ? (
        <div style={{ textAlign: 'center', padding: '60px', color: theme.textMuted }}>No data available</div>
      ) : (
        <div style={{ height: 320 }}>
          <canvas ref={canvasRef} />
        </div>
      )}
    </div>
  );
};

/* ─── Stat box ─── */
const StatBox = ({ label, value, valueColor }) => {
  const theme = getTheme();
  return (
  <div style={{
    background: theme.surface2, borderRadius: 10, padding: '12px 16px',
    flex: 1, minWidth: 0,
  }}>
    <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label}
    </div>
    <div style={{ fontSize: 20, fontWeight: 700, color: valueColor || theme.text, fontFamily: "'DM Mono', monospace" }}>
      {value}
    </div>
  </div>
);};

/* ─── Data Table Component ─── */
const DataTable = ({ data, theme }) => {
  const secToHHMM = (s) => {
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    
    if (d > 0) {
      return `${d}d ${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
    } else if (h > 0) {
      return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
    } else if (m > 0) {
      return `${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
    } else {
      return `${sec}s`;
    }
  };

  if (!data?.length) {
    return (
      <div style={{
        background: theme.surface, border: `1px solid ${theme.border}`,
        borderRadius: 16, padding: '24px 28px', marginBottom: 28,
      }}>
        <div style={{ textAlign: 'center', padding: '60px', color: theme.textMuted }}>
          No data available
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: theme.surface, border: `1px solid ${theme.border}`,
      borderRadius: 16, padding: '24px 28px', marginBottom: 28,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
        <span style={{ fontSize: 22, color: theme.accent }}>📊</span>
        <span style={{
          background: theme.surface2, border: `1px solid ${theme.border}`,
          borderRadius: 6, padding: '2px 10px',
          color: theme.text, fontWeight: 600, fontSize: 15,
        }}>Detailed Data</span>
      </div>
      
      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 14,
          fontFamily: "'DM Sans', sans-serif"
        }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${theme.border}` }}>
              <th style={{ padding: '12px', textAlign: 'left', color: theme.text, fontWeight: 600 }}>Catalogue Name</th>
              <th style={{ padding: '12px', textAlign: 'left', color: theme.text, fontWeight: 600 }}>POS ID</th>
              <th style={{ padding: '12px', textAlign: 'left', color: theme.text, fontWeight: 600 }}>POS Name</th>
              <th style={{ padding: '12px', textAlign: 'left', color: theme.text, fontWeight: 600 }}>Merchant ID</th>
              <th style={{ padding: '12px', textAlign: 'left', color: theme.text, fontWeight: 600 }}>Total Running Time</th>
              <th style={{ padding: '12px', textAlign: 'left', color: theme.text, fontWeight: 600 }}>Avg Running Time</th>
              <th style={{ padding: '12px', textAlign: 'left', color: theme.text, fontWeight: 600 }}>Avg Interrupts</th>
              <th style={{ padding: '12px', textAlign: 'left', color: theme.text, fontWeight: 600 }}>Avg Cycles</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <tr key={index} style={{ 
                borderBottom: `1px solid ${theme.border}`,
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme.surface2;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}>
                <td style={{ padding: '12px', color: theme.text }}>{item.catalogname || '-'}</td>
                <td style={{ padding: '12px', color: theme.text }}>{item.posid || '-'}</td>
                <td style={{ padding: '12px', color: theme.text }}>{item.posname || '-'}</td>
                <td style={{ padding: '12px', color: theme.text }}>{item.merchantid || '-'}</td>
                <td style={{ padding: '12px', color: theme.accent, fontFamily: "'DM Mono', monospace" }}>
                  {secToHHMM(item.totalrunningtime || 0)}
                </td>
                <td style={{ padding: '12px', color: theme.text, fontFamily: "'DM Mono', monospace" }}>
                  {secToHHMM(item.avgrunningtimeinseconds || 0)}
                </td>
                <td style={{ padding: '12px', color: theme.danger, fontFamily: "'DM Mono', monospace" }}>
                  {(item.avginterruptscount || 0).toFixed(2)}
                </td>
                <td style={{ padding: '12px', color: theme.info, fontFamily: "'DM Mono', monospace" }}>
                  {(item.avgcompletecyclecount || 0).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ─── Schedule Card (Expected vs Actual) ─── */
const ScheduleCard = ({ Schedule, color, secToHHMM, isReport9, onClick }) => {
  const theme = getTheme();
  const scheduled = Schedule.scheduledtimeinseconds || Schedule.scheduletotaltime || Math.round((Schedule.totalrunningtime || 0) * 1.35);
  const played    = Schedule.totalrunningtime || Schedule.avgrunningtimeinseconds || 0;
  const pct       = scheduled > 0 ? Math.round((played / scheduled) * 100) : 0;
  const mediaType = Schedule.mediatype || (Math.random() > 0.5 ? 'video' : 'image');

  return (
    <div 
      onClick={onClick}
      style={{
        background: theme.surface, border: `1px solid ${theme.border}`,
        borderRadius: 12, padding: '12px 14px',
        display: 'flex', flexDirection: 'column', gap: 10,
        minWidth: 0,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Title row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <span style={{
              width: 10, height: 10, borderRadius: '50%',
              background: color, display: 'inline-block', flexShrink: 0,
            }} />
            <span style={{ fontWeight: 700, fontSize: 15, color: theme.text }}>
              {Schedule.catalogname || Schedule.Schedulename}
            </span>
          </div>
          <span style={{
            background: mediaType === 'video' ? 'rgba(59,130,246,0.15)' : 'rgba(245,158,11,0.15)',
            color: mediaType === 'video' ? theme.info : theme.warning,
            border: `1px solid ${mediaType === 'video' ? 'rgba(59,130,246,0.3)' : 'rgba(245,158,11,0.3)'}`,
            borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 500,
            display: 'inline-flex', alignItems: 'center', gap: 3,
          }}>
            {mediaType === 'video' ? '▦' : '⊞'} {mediaType}
          </span>
        </div>
        {isReport9 && <DonutChart pct={Math.min(pct, 100)} color={color} />}
      </div>

      {/* Stats row 1 */}
      <div style={{ display: 'flex', gap: 6 }}>
        <StatBox label="Scheduled" value={secToHHMM(scheduled)} />
        <StatBox label="Played"    value={secToHHMM(played)}    valueColor={theme.accent} />
      </div>

      {/* Stats row 2 */}
      <div style={{ display: 'flex', gap: 6 }}>
        <StatBox label="Cycles"     value={(Schedule.avgcompletecyclecount || Schedule.completecyclecountsum || 0).toFixed(0)} />
        <StatBox label="Interrupts" value={(Schedule.avginterruptscount    || Schedule.interruptscountsum    || 0).toFixed(0)} valueColor={theme.danger} />
      </div>
    </div>
  );
};

/* ─── Main Dashboard ─── */
const ScheduleDashboard = () => {
  // Force recompilation - remove this line after fixing
  console.log('ScheduleDashboard component loaded');
  const theme = getTheme();
  const navigate = useNavigate();
  const [period, setPeriod]       = useState('thisweek');
  const [loading, setLoading]     = useState(false);
  const [Report1Data, setReport1] = useState(null);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [Schedules, setSchedules]   = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState('');
  const [ScheduleLoading, setScheduleLoading] = useState(false);
  
  // Add catalog state variables
  const [catalogs, setCatalogs] = useState([]);
  const [selectedCatalog, setSelectedCatalog] = useState('');
  const [catalogLoading, setCatalogLoading] = useState(false);
  const isMounted = useRef(false);

  const API_BASE   = 'https://api.dqr-display-tms.eventum.co.in/api/SchedulerStatistics';
  const AUTH_TOKEN = getCookie("token");

  // API call for GET requests (for Schedules)
  const callGetAPI = async (url) => {
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'accept': 'text/plain',
          'UserId': getCookie("userid"),
          'X-Authorization': AUTH_TOKEN
        }
      });
      
      const result = await response.text();
      return JSON.parse(result);
    } catch (error) {
      console.error('Error calling GET API:', error);
      return { isresponse: false, errorcode: 'CLIENT_ERROR', errormsg: error.message };
    }
  };

  // Load Catalogs from API
  const loadCatalogs = async () => {
    setCatalogLoading(true);
    try {
      const res = await callGetAPI('https://api.dqr-display-tms.eventum.co.in/api/CatalogOperation/GetDistinct/catalogname');
      if (res.isresponse && res.data) setCatalogs(res.data);
    } finally { setCatalogLoading(false); }
  };

  // Load Schedules from API
  const loadSchedules = async () => {
    setScheduleLoading(true);
    try {
      const res = await callGetAPI('https://api.dqr-display-tms.eventum.co.in/api/CatalogSchedulerOperation/GetDistinct/name');
      if (res.isresponse && res.data) setSchedules(res.data);
    } finally { setScheduleLoading(false); }
  };

  // Load Catalog Report
  const loadCatalogReport = async (catalogId, p) => {
    if (!catalogId) return;
    setLoading(true);
    const payload = {
      ...getDateRange(p),
      catalogid: parseInt(catalogId)
    };

    try {
      const response = await fetch(`${API_BASE}/Report10`, {
        method: 'POST',
        headers: {
          'accept': 'text/plain',
          'UserId': getCookie("userid"),
          'X-Authorization': AUTH_TOKEN,
          'Content-Type': 'application/json-patch+json'
        },
        body: JSON.stringify(payload)
      });
      
      const result = await response.text();
      const res = JSON.parse(result);
      
      console.log('Catalogue Report10:', res);
      
      if (res.isresponse && res.data) {
        setReport1(res);
      }

    } catch (error) {
      console.error('Error calling Catalogue report:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load Schedule Report
  const loadScheduleReport = async (scheduleId, catalogId, p) => {
    if (!scheduleId) return;
    setLoading(true);
    const payload = {
      ...getDateRange(p),
      catalogid: catalogId ? parseInt(catalogId) : 0,
      scheduleid: parseInt(scheduleId)
    };

    try {
      const response = await fetch(`${API_BASE}/Report11`, {
        method: 'POST',
        headers: {
          'accept': 'text/plain',
          'UserId': getCookie("userid"),
          'X-Authorization': AUTH_TOKEN,
          'Content-Type': 'application/json-patch+json'
        },
        body: JSON.stringify(payload)
      });
      
      const result = await response.text();
      const res = JSON.parse(result);
      
      console.log('Schedule Report11:', res);
      
      if (res.isresponse && res.data) {
        setReport1(res);
      }

    } catch (error) {
      console.error('Error calling schedule report:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle catalog change
  const handleCatalogChange = async (catalogId) => {
    if (catalogId === selectedCatalog) return;
    
    setSelectedCatalog(catalogId);
    setSelectedSchedule(''); // Clear schedule when catalog is selected
    
    if (!catalogId) {
      setReport1(null);
      setLoading(false);
      return;
    }

    await loadCatalogReport(catalogId, period);
  };

  // Handle schedule change
  const handleScheduleChange = async (scheduleId) => {
    if (scheduleId === selectedSchedule) return;
    
    setSelectedSchedule(scheduleId);
    setSelectedCatalog(''); // Clear catalog when schedule is selected
    
    if (!scheduleId) {
      setReport1(null);
      setLoading(false);
      return;
    }

    await loadScheduleReport(scheduleId, '', period);
  };

  const getDateRange = (p) => {
    const now = new Date();
    if (p === 'all') return { startdate: 0, enddate: 0, isalltime: true };
    if (p === 'custom') {
      if (customStartDate && customEndDate) {
        const start = new Date(customStartDate);
        const end = new Date(customEndDate);
        end.setHours(23, 59, 59, 999); // End of day
        return { 
          startdate: Math.floor(start.getTime() / 1000), 
          enddate: Math.floor(end.getTime() / 1000), 
          isalltime: false 
        };
      }
      return { startdate: 0, enddate: 0, isalltime: false };
    }
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    const enddate = Math.floor(endOfDay.getTime() / 1000);
    let startdate;
    if (p === 'today') {
      startdate = Math.floor(new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0).getTime() / 1000);
    } else if (p === 'thisweek') {
      // Get current week (Sunday to Saturday)
      const dayOfWeek = now.getDay(); // 0 = Sunday, 6 = Saturday
      const sunday = new Date(now);
      sunday.setDate(now.getDate() - dayOfWeek);
      sunday.setHours(0, 0, 0, 0);
      startdate = Math.floor(sunday.getTime() / 1000);
    } else if (p === 'week') {
      startdate = Math.floor((now.getTime() - 7 * 86400000) / 1000);
    } else if (p === 'last7days') {
      startdate = Math.floor((now.getTime() - 7 * 86400000) / 1000);
    } else if (p === 'last30days') {
      startdate = Math.floor((now.getTime() - 30 * 86400000) / 1000);
    } else {
      startdate = Math.floor((now.getTime() - 30 * 86400000) / 1000);
    }
    return { startdate, enddate, isalltime: false };
  };

  const callAPI = async (endpoint, payload) => {
    try {
      const res = await fetch(`${API_BASE}/${endpoint}`, {
        method: 'POST',
        headers: {
          'accept': 'text/plain',
          'UserId': getCookie("userid"),
          'X-Authorization': AUTH_TOKEN,
          'Content-Type': 'application/json-patch+json',
        },
        body: JSON.stringify(payload),
      });
      return JSON.parse(await res.text());
    } catch (e) {
      return { isresponse: false, errormsg: e.message };
    }
  };

  const secToHHMM = (s) => {
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    
    if (d > 0) {
      return `${d}d ${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
    } else if (h > 0) {
      return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
    } else if (m > 0) {
      return `${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
    } else {
      return `${sec}s`;
    }
  };

  const chartData = Report1Data?.data || [];

  const TABS = [
    { key: 'today', label: 'Today' },
    { key: 'thisweek', label: 'This Week' },
    { key: 'last7days', label: 'Last 7 Days' },
    { key: 'last30days', label: 'Last 30 Days' },
    { key: 'month', label: 'This Month' },
    { key: 'all',   label: 'All Time' },
    { key: 'custom', label: 'Custom' },
  ];

  /* Google Font */
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500;700&display=swap';
    document.head.appendChild(link);
    
    // Load Catalogs and Schedules on component mount
    loadCatalogs();
    loadSchedules();
  }, []);

  // Re-load data when period changes
  useEffect(() => {
    if (isMounted.current) {
      if (selectedCatalog) {
        loadCatalogReport(selectedCatalog, period);
      } else if (selectedSchedule) {
        loadScheduleReport(selectedSchedule, selectedCatalog, period);
      }
    } else {
      isMounted.current = true;
    }
  }, [period]);

  const selectStyle = {
    backgroundColor: theme.surface,
    color: theme.text,
    border: `1px solid ${theme.border}`,
    borderRadius: 6,
    padding: '8px 12px',
    fontSize: 14,
    outline: 'none',
    minWidth: 200,
    fontFamily: "'DM Sans', sans-serif",
  };

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <div style={{ padding: '32px 24px' }}>
            {/* Catalog and Schedule Selectors */}
            <div style={{ marginBottom: 28 }}>
              <div className="card card-secondary card-outline">
                <div className="card-header">
                  <h3 className="card-title">
                    <i className="fas fa-filter mr-1"></i> Filters
                  </h3>
                </div>
                <div className="card-body">
                  <div className="row align-items-center">
                    <div className="col-auto">
                      <label className="text-secondary font-weight-bold mb-1">
                        <i className="fas fa-store mr-1"></i> Catalogue
                      </label>
                      <select
                        value={selectedCatalog}
                        disabled={catalogLoading}
                        onChange={(e) => handleCatalogChange(e.target.value)}
                        style={selectStyle}
                      >
                        <option value="">All Catalogues</option>
                        {catalogs.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.value}
                          </option>
                        ))}
                      </select>
                      {catalogLoading && (
                        <small className="text-muted ml-2">Loading...</small>
                      )}
                    </div>

                    <div className="col-auto">
                      <label className="text-secondary font-weight-bold mb-1">
                        <i className="fas fa-calendar-alt mr-1"></i> Schedule
                      </label>
                      <select
                        value={selectedSchedule}
                        disabled={ScheduleLoading}
                        onChange={(e) => handleScheduleChange(e.target.value)}
                        style={selectStyle}
                      >
                        <option value="">All Schedules</option>
                        {Schedules.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.value}
                          </option>
                        ))}
                      </select>
                      {ScheduleLoading && (
                        <small className="text-muted ml-2">Loading...</small>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Page header */}
            <section className="content-header">
              <div className="container-fluid">
                <div className="row mb-2">
                  <div className="col-sm-6">
                    <h1 className="m-0">◎ Catalogue Statistics</h1>
                  </div>
                  <div className="col-sm-6">
                    <ol className="breadcrumb float-sm-right">
                      <li className="breadcrumb-item">
                        <Link to="/" style={{ color: 'var(--primary)' }}>
                          Home
                        </Link>
                      </li>
                      <li className="breadcrumb-item">
                        <Link to="/catalog-dashboard" style={{ color: 'var(--primary)' }}>
                          Catalogue Dashboard
                        </Link>
                      </li>
                      <li className="breadcrumb-item active">Catalogue Statistics</li>
                    </ol>
                  </div>
                </div>
              </div>
            </section>

            <section className="content">
              <div className="container-fluid">
                {/* Period tabs */}
                <div className="card mb-3">
                  <div className="card-body p-2">
                    <div className="btn-group w-100" role="group">
                      {TABS.map((t) => (
                        <button
                          key={t.key}
                          type="button"
                          className={`btn ${period === t.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => setPeriod(t.key)}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <div className="text-muted mt-2">Loading statistics…</div>
                  </div>
                ) : (
                  <>
                    {/* Bar chart (Schedule Performance) */}
                    <CatelogStChart data={chartData} period={period} />
                    
                    {/* Data Table */}
                    <DataTable data={chartData} theme={theme} />
                  </>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default ScheduleDashboard;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}